<script lang="ts">
  import { icon } from '#app/lib/icons'
  import { scrollToElement } from '#app/lib/screen'
  import type { WorkEditionFilters } from '#entities/components/lib/editions_list_actions_helpers'
  import type { SerializedItemWithUserData } from '#inventory/lib/items'
  import { I18n, i18n } from '#user/lib/i18n'
  import ItemsByCategory from './items_by_category.svelte'
  import { categoriesHeaders, sortItemsByCategoryAndDistance } from './items_lists_helpers.ts'

  const {
    filteredItems,
    allItems,
    displayCover,
    waitingForItems,
    showItemsOnMap,
    workEditionFilters: filters,
  }: {
    filteredItems: SerializedItemWithUserData[]
    allItems: SerializedItemWithUserData[]
    displayCover: boolean
    waitingForItems: Promise<unknown>
    showItemsOnMap: (items: SerializedItemWithUserData[]) => void
    workEditionFilters: WorkEditionFilters
  } = $props()

  let filtersTopEl: HTMLElement = $state()
  function resetFilter (name: keyof WorkEditionFilters) {
    filters[name] = 'all'
    // Mitigate a scroll jump due to the edition list above being updated
    scrollToElement(filtersTopEl, { marginTop: 32 })
  }

  const itemsByCategories = $derived(filteredItems ? sortItemsByCategoryAndDistance(filteredItems) : {})
  const hasActiveFilter = $derived(filters?.selectedLangLabel || filters?.selectedPublisherLabel)
</script>

<div class="filters-top" bind:this={filtersTopEl}></div>
{#if hasActiveFilter}
  <div class="filters-wrapper">
    <div class="filters">
      <span>{i18n('Active filters')}</span>
      {#if filters.selectedLangLabel}
        <button
          class="tiny-button grey"
          title={I18n('reset filter')}
          aria-controls="language-filter"
          onclick={() => resetFilter('selectedLang')}
        >
          {@html icon('close')}
          {i18n('language')}:
          {filters.selectedLangLabel}
        </button>
      {/if}
      {#if filters.selectedPublisherLabel}
        <button
          class="tiny-button grey"
          title={I18n('reset filter')}
          aria-controls="publisher-filter"
          onclick={() => resetFilter('selectedPublisher')}
        >
          {@html icon('close')}
          {i18n('publisher')}:
          {filters.selectedPublisherLabel}
        </button>
      {/if}
      {#if filters.selectedPublicationYear !== 'all'}
        <button
          class="tiny-button grey"
          title={I18n('reset filter')}
          aria-controls="publication-year-filter"
          onclick={() => resetFilter('selectedPublicationYear')}
        >
          {@html icon('close')}
          {i18n('publication year')}:
          {filters.selectedPublicationYear}
        </button>
      {/if}
      <div class="counter" title={`${i18n('Filtered items')} / ${i18n('All items')}`}>
        {filteredItems.length} / {allItems.length}
      </div>
    </div>
  </div>
{/if}

<div class="items-lists">
  {#each Object.keys(categoriesHeaders) as category (category)}
    <ItemsByCategory
      {category}
      {displayCover}
      {waitingForItems}
      headers={categoriesHeaders[category]}
      categoryItems={itemsByCategories[category]}
      {showItemsOnMap}
    />
  {/each}
</div>

<style lang="scss">
  @import "general/scss/utils";
  .items-lists{
    margin: auto;
    inline-size: 100%;
    max-width: 50em;
  }
  // Use a wrapper to be able to have flex-grow up to max-width AND margin=auto
  .filters-wrapper{
    align-self: stretch;
    @include display-flex(row, stretch);
  }
  .filters{
    max-inline-size: 50em;
    flex: 1;
    margin: 0.5em auto;
    padding: 0.5em;
    background-color: $light-grey;
    @include radius;
    align-self: stretch;
    @include display-flex(row, center, flex-start);
    span{
      color: $label-grey;
    }
    button{
      margin: 0.5em;
      padding: 0.3em 0.4em 0.3em 0;
    }
  }
  .counter{
    font-weight: bold;
  }
</style>
